import { useState } from 'react';
import { toast } from 'sonner';

import { rpc } from '…/app/common/rpc/client.mts';
import { Combobox } from '…/app/common/Combobox/Combobox.tsx';
import { Button } from '…/app/common/Button/Button.tsx';
import type { Workspace, WorkspaceId } from '…/app/state/states.d.ts';

import { searchWorkspaces } from '../queries/searchWorkspaces.op.mts';
import { addMeToWorkspace } from '../queries/addMeToWorkspace.op.mts';

const MAX_WORKSPACES = 26;

type WorkspaceOption = {
  label: string;
  value: string;
};

export function AddWorkspaceCard({
  workspaces,
}: {
  workspaces: Map<WorkspaceId, Workspace>;
}) {
  const [selectedWorkspace, setSelectedWorkspace] = useState<WorkspaceOption | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const workspaceIds: WorkspaceId[] = Array.from(workspaces.entries()).map(([id]) => id);

  const handleWorkspaceSearch = async (query?: string) => {
    if (!query || query.length < 3) {
      return [];
    }

    try {
      const results = await searchWorkspaces({ search: query });
      return results
        .filter((workspace) => !workspaceIds.includes(workspace.id))
        .map((workspace) => ({
          label: workspace.name,
          value: workspace.id,
        }));
    } catch (error) {
      console.error('Failed to search workspaces:', error);
      return [];
    }
  };

  const handleAddWorkspace = async () => {
    if (!selectedWorkspace) return;

    setIsLoading(true);
    try {
      await addMeToWorkspace({ workspaceId: selectedWorkspace.value });
      toast.success('Workspace added successfully');

      // Refresh tokens and reload the page to show the new workspace
      await rpc.post('proxy/refresh-tokens', {
        json: { orb: true },
      });

      if (window) window.location.reload();
    } catch (error) {
      console.error('Failed to add workspace:', error);
      toast.error('Failed to add Workspace');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="w-full p-6 bg-white rounded-lg shadow-sm max-w-md">
      <h2 className="text-lg text-center font-medium text-gray-700 mb-4">
        Search for a Workspace
      </h2>

      {workspaceIds.length > MAX_WORKSPACES && (
        <div className="rounded-md text-yellow-800 bg-yellow-100 p-4 mb-4 text-sm">
          You need to remove yourself from some Workspaces before you can add yourself to a new one.
        </div>
      )}

      <div className="mb-4">
        <Combobox
          name="workspace-search"
          onChange={(value) => {
            setSelectedWorkspace(value);
          }}
          placeholder="Type to search (min 3 characters)"
          searchFn={handleWorkspaceSearch}
          value={selectedWorkspace}
        />
      </div>

      <Button
        className="w-full"
        disabled={!selectedWorkspace || workspaceIds.length > MAX_WORKSPACES}
        loading={isLoading}
        onClick={handleAddWorkspace}
      >
        Add yourself to this Workspace
      </Button>
    </div>
  );
}

AddWorkspaceCard.displayName = 'AddWorkspaceCard';
